
import { Component, Prop, VModel, Vue, Watch } from 'vue-property-decorator';
import {
    AuditEscalationGroupModel,
    EscalationGroupTypeEnum,
    GroupModel,
    GroupUserModel,
    QueryOperator,
    TenantUserModel,
} from '@/libs/Api';
import { auditEscalationGroupsStore } from '@/libs/audit-escalation-groups/+state/store';
import { CrudAction } from '@/libs/core/+state/models/crud-action';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { groupsStore } from '@/libs/groups/+state/store';
import { tenantUsersStore } from '@/libs/tenants/+state/store';
import { Guid } from '@/libs/common/functions/guid';

@Component({})
export default class WorkTaskEscalation extends Vue {
    @VModel() tenantUsers!: string[];
    @Prop({ type: String, required: true }) auditId!: string;
    @Prop({ type: String, required: true }) categoryId!: string;
    @Prop({ required: true }) escalationGroupType!: EscalationGroupTypeEnum;
    @Prop({ default: false }) readonly!: boolean;
    @Prop({ default: true }) required!: boolean;
    @Prop({ default: false }) large!: boolean;
    protected selectedTenantUsers: string[] = [];
    protected selection: string[] = [];
    protected customDialog = false;
    protected groups: GroupModel[] = [];
    protected basicRules = [(v: any) => !!v || this.$t('validation.thisFieldIsRequired')];

    @Watch('tenantUsers')
    tenantUsersChanged(v: string[]) {
        this.selectedTenantUsers = v;
    }

    protected get validationUsers(): string {
        return this.selectedTenantUsers.join(', ');
    }

    protected get escalationGroups(): AuditEscalationGroupModel[] {
        return auditEscalationGroupsStore.useGetter(CrudGetter.Data, `${this.auditId}:${this.escalationGroupType}`);
    }

    mounted() {
        auditEscalationGroupsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                undefined,
                [
                    { field: 'auditId', op: QueryOperator.Eq, comparand: this.auditId },
                    {
                        field: 'escalationGroupType',
                        op: QueryOperator.Eq,
                        comparand: this.escalationGroupType,
                    },
                ],
                undefined,
                false,
                `${this.auditId}:${this.escalationGroupType}`
            )
        );
        this.groups = groupsStore.useGetter(CrudGetter.Data);
    }

    protected onCustomClick() {
        const selection: string[] = [];
        const escalationGroups =
            (auditEscalationGroupsStore.useGetter(
                CrudGetter.Data,
                `${this.auditId}:${this.escalationGroupType}`
            ) as AuditEscalationGroupModel[]) ?? [];
        escalationGroups.forEach((escalationGroup) => {
            const groupUsers = this.getGroupUsers(escalationGroup);
            const filteredGroupUsers = groupUsers.filter((e) => this.selectedTenantUsers.contains(e.tenantUserId!));
            for (const e of filteredGroupUsers) {
                if (!selection.includes(e.tenantUserId!)) {
                    selection.push(e.tenantUserId!);
                }
            }
        });
        this.selection = selection;
        this.customDialog = true;
    }

    protected onCloseClick() {
        this.selection = [];
        this.customDialog = false;
    }

    protected onSaveClick() {
        this.customDialog = false;
        const tenantUsers: string[] = [];
        const escalationGroups =
            (auditEscalationGroupsStore.useGetter(
                CrudGetter.Data,
                `${this.auditId}:${this.escalationGroupType}`
            ) as AuditEscalationGroupModel[]) ?? [];
        escalationGroups.forEach((escalationGroup) => {
            const groupUsers = this.getGroupUsers(escalationGroup);
            for (const groupUser of groupUsers) {
                if (this.selection.indexOf(groupUser.tenantUserId!) > -1 && !tenantUsers.contains(groupUser.tenantUserId!)) {
                    tenantUsers.push(groupUser.tenantUserId!);
                }
            }
        });
        this.selectedTenantUsers = tenantUsers;
        this.updateInputValue();
    }

    protected isGroupEnabled(escalationGroup: AuditEscalationGroupModel): boolean {
        return this.getGroupUsers(escalationGroup).length > 0;
    }

    protected getGroupUsers(escalationGroup: AuditEscalationGroupModel): GroupUserModel[] {
        let groupUsers = this.groups.find((x) => x.id == escalationGroup.groupId)?.groupUsers ?? [];
        const tenantUsers = [...(tenantUsersStore.useGetter(CrudGetter.Data) as TenantUserModel[])];
        groupUsers = groupUsers.filter((x) => {
            if (x.tenantUserId == null) {
                return false;
            }
            const tenantUser = tenantUsers.find((u) => u.id == x.tenantUserId);
            if (!tenantUser) {
                return false;
            }
            return (
                tenantUser.accessAllCategories || tenantUser.categories?.find((c) => c.categoryId == this.categoryId) != null
            );
        });
        return groupUsers;
    }

    protected isGroupSelected(escalationGroup: AuditEscalationGroupModel) {
        const groupUsers = this.getGroupUsers(escalationGroup);
        return groupUsers.every((x) => this.selection.contains(x.tenantUserId!));
    }

    protected isEscalationGroupSelected(escalationGroup: AuditEscalationGroupModel) {
        const groupUsers = this.getGroupUsers(escalationGroup);
        return groupUsers.every((x) => this.selectedTenantUsers.contains(x.tenantUserId!));
    }

    protected isEscalationGroupPartiallySelected(escalationGroup: AuditEscalationGroupModel) {
        const groupUsers = this.getGroupUsers(escalationGroup);
        const selectedCount = groupUsers.filter((x) => this.selectedTenantUsers.contains(x.tenantUserId!)).length;
        return selectedCount > 0 && selectedCount < groupUsers.length;
    }

    protected isEscalationGroupPartiallySelectedCount(escalationGroup: AuditEscalationGroupModel) {
        const groupUsers = this.getGroupUsers(escalationGroup);
        return groupUsers.filter((x) => this.selectedTenantUsers.contains(x.tenantUserId!)).length;
    }

    protected isGroupUserSelected(tenantUserId: string): boolean {
        return this.selection.contains(tenantUserId);
    }

    protected selectOrDeselectGroup(escalationGroup: AuditEscalationGroupModel) {
        if (this.readonly) {
            return;
        }
        const groupUsers = this.getGroupUsers(escalationGroup);
        if (this.isEscalationGroupSelected(escalationGroup)) {
            for (const groupUser of groupUsers) {
                if (this.selectedTenantUsers.contains(groupUser.tenantUserId!)) {
                    this.selectedTenantUsers.splice(this.selectedTenantUsers.indexOf(groupUser.tenantUserId!), 1);
                }
            }
        } else {
            for (const groupUser of groupUsers) {
                if (!this.selectedTenantUsers.contains(groupUser.tenantUserId!)) {
                    this.selectedTenantUsers.push(groupUser.tenantUserId!);
                }
            }
        }
        this.updateInputValue();
    }

    protected selectOrDeselectDialogGroup(isSelected: boolean, escalationGroup: AuditEscalationGroupModel) {
        if (this.readonly) {
            return;
        }
        const groupUsers = this.getGroupUsers(escalationGroup);
        if (isSelected) {
            for (const groupUser of groupUsers) {
                if (!this.selection.contains(groupUser.tenantUserId!)) {
                    this.selection.push(groupUser.tenantUserId!);
                }
            }
        } else {
            for (const groupUser of groupUsers) {
                if (this.selection.contains(groupUser.tenantUserId!)) {
                    this.selection.splice(this.selection.indexOf(groupUser.tenantUserId!), 1);
                }
            }
        }
    }

    protected selectOrDeselectDialogUser(isSelected: boolean, groupUser: GroupUserModel) {
        if (this.readonly) {
            return;
        }
        if (isSelected) {
            if (!this.selection.contains(groupUser.tenantUserId!)) {
                this.selection.push(groupUser.tenantUserId!);
            }
        } else {
            if (this.selection.contains(groupUser.tenantUserId!)) {
                this.selection.splice(this.selection.indexOf(groupUser.tenantUserId!), 1);
            }
        }
    }

    private updateInputValue() {
        this.$emit('input', [...this.selectedTenantUsers]);
    }
}
